/**
 * CreatureService (architecture §3.7): `active()` / `get(id)` over the `creature` save slice, and the P1 half of
 * `AnimService` — `prepare(dna, opts)` (rig + mesh + look, cached by (DNA code, lod, context, scale)). The animation half
 * (clips, worlds, planGroup, pets, bosses) is P2's; until then those members throw a clear error.
 */
import type { AnimService, CreatureDNA, CreatureLookData, CreatureMeshData, CreatureService, Rig, Store } from '../shared';
import { dnaCodec, dnaFromFields, speciesFields } from './dna';
import { buildCreature } from './mesh';
import { lookFromAppearance } from './look';
import type { CreatureSave } from './save';
import { creaturePortrait } from './screens/portrait';
import { createAtelierActor, atelierPreset } from './atelier/runtime/actor';

type Prepared = { rig: Rig; mesh: CreatureMeshData; look: CreatureLookData };
const cache = new Map<string, Prepared>();
const CACHE_MAX = 16;

/** Rig + mesh + look for rendering, cached (LRU of 16) by (DNA code, lod, context, scale, tint). */
export function prepareCreature(dna: CreatureDNA, opts: { lod: 0 | 1 | 'hero'; context: 'battle' | 'showcase'; shadowTint: [number, number, number]; scale?: number }): Prepared {
  const key = `${dnaCodec.encode(dna)}|${opts.lod}|${opts.context}|${opts.scale ?? 1}|${opts.shadowTint.join(',')}`;
  const hit = cache.get(key);
  if (hit) { cache.delete(key); cache.set(key, hit); return hit; }
  const c = buildCreature(dna, { lod: opts.lod, battle: opts.context === 'battle' });
  const out: Prepared = { rig: c.built.rig, mesh: c.mesh, look: lookFromAppearance(dna, opts.shadowTint, c.built) };
  cache.set(key, out);
  if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value!);
  return out;
}

const p2 = (name: string) => (): never => { throw new Error(`AnimService.${name} is implemented by P2 (animation runtime)`); };

export function createCreatureService(store: Pick<Store, 'state'>): CreatureService {
  const slice = (): CreatureSave => store.state.slices.creature as CreatureSave;
  const decode = (code: string | undefined): CreatureDNA | null => (code ? dnaCodec.decode(code) : null);
  const anim: AnimService = {
    get clips(): never { return p2('clips')(); },
    loadFamilies: async () => { /* P2 */ },
    createWorld: p2('createWorld'),
    prepare: (dna, opts) => prepareCreature(dna, opts),
    preparePet: p2('preparePet'),
    prepareBoss: p2('prepareBoss'),
    planGroup: p2('planGroup'),
    predictClass: p2('predictClass'),
    pickVariant: p2('pickVariant'),
  };
  return {
    appearance(id){const value=slice().roster.find(row=>row.id===id)?.appearance;return value?structuredClone(value):null;},
    createAtelierActor, atelierPreset,
    createPreset: (species, opts) => dnaFromFields(speciesFields(species, { look: opts.look }), opts.name, opts.soulSeed),
    portrait(dna) {
      const row = slice().roster.find(r => r.dna === dnaCodec.encode(dna));
      return creaturePortrait(dna, row?.portrait);
    },
    active() {
      const s = slice();
      const id = s.activeId;
      const row = id ? s.roster.find((r) => r.id === id) : undefined;
      const dna = decode(row?.dna);
      return row && dna ? { id: row.id, dna } : null;
    },
    get(id: string) {
      const row = slice().roster.find((r) => r.id === id);
      const dna = decode(row?.dna);
      return row && dna ? { id: row.id, dna } : null;
    },
    anim,
  };
}
